/**
 * Best-effort haptic feedback.
 *
 * - Android / Chromium: Vibration API.
 * - iOS Safari: there is NO Vibration API. On iOS 17.4–26.4 the system
 *   "switch" tick plays when a `<label>` associated with a native
 *   `<input type="checkbox" switch>` control is clicked — the click MUST go
 *   through the label; clicking the input directly is silent. The element
 *   must be rendered (display:none suppresses it). Apple removed this
 *   behavior in iOS 26.5+, where no web-reachable haptic currently exists —
 *   there this degrades to a silent no-op.
 */

// undefined = not probed yet; null = unsupported environment.
let hapticLabel: HTMLLabelElement | null | undefined;

function getHapticLabel(): HTMLLabelElement | null {
  if (hapticLabel !== undefined) {
    return hapticLabel;
  }
  if (typeof document === 'undefined' || !document.body) {
    return null; // don't cache — body may exist on a later call
  }
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.setAttribute('switch', '');
  input.tabIndex = -1;
  const label = document.createElement('label');
  label.setAttribute('aria-hidden', 'true');
  label.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
  label.appendChild(input);
  document.body.appendChild(label);
  hapticLabel = label;
  return label;
}

/**
 * Play a short haptic tick. `durationMs` only applies where the Vibration API
 * exists; the iOS switch tick has a fixed system intensity.
 */
export function hapticTick(durationMs = 10): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(durationMs);
      return;
    }
    getHapticLabel()?.click();
  } catch {
    // Haptics are decorative — never throw into a gesture handler.
  }
}
